import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Modal, Nav, Row, Spinner } from "react-bootstrap";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { mediaUrl } from "../../api/axios";
import { isTradeableCard } from "../../utils/cardAvailability";
import { binderQuantity, binderQuantityLabel } from "../../utils/binderQuantity";
import "./SharedBinderActions.css";
import { RarityMark } from "./RarityMark";
import BinderCardViewer from "./BinderCardViewer";
import { useFeedbackMessage } from "../Common/Feedback/FeedbackHub";
import { ProfileSkeleton } from "../Common/Loading/PageSkeletons";
import { postTypeClass, postTypes } from "../../utils/feedPresentation";

const intentionLabels = { collection: "En coleccion", open_to_trade: "Acepta propuestas", for_trade: "Para intercambio" };
const cardFinishLabels = { normal: "Normal", holo: "Holo", reverse_holo: "Reverse holo", cosmos_holo: "Cosmos holo", stamped: "Stamped", other: "Otro" };
const binderThemeLabels = { character: "Personaje", artist: "Artista", rarity: "Rareza", card_type: "Tipo de carta", energy_type: "Tipo elemental", custom: "Otro tema" };
const binderThemeText = collection => collection?.theme_query ? `${binderThemeLabels[collection.theme_type] || "Tema"}: ${collection.theme_query}` : "";

const statusLabels = {
  showcase: "En coleccion",
  favorite: "Favorita / no intercambio",
  for_trade: "Para intercambio",
  wanted: "Buscada",
  for_sale: "Para intercambio"
};

const BINDER_CARDS_PER_PAGE = 9;
const normalizeSharedBinderFilter = value => ["duplicates", "trade", "wanted", "missing", "owned"].includes(value) ? value : "all";
const newestPhotoFirst = photos => [...(photos || [])].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
const cardFrontPhoto = card => {
  const photos = newestPhotoFirst(card?.photos);
  return photos.find(photo => ["front", "binder"].includes(photo.photo_type));
};
const cardBackPhoto = card => newestPhotoFirst(card?.photos).find(photo => photo.photo_type === "back");
const cardFrontImage = card => mediaUrl(cardFrontPhoto(card)?.photo_url || card?.image_url);
const cardBackImage = card => cardBackPhoto(card) ? mediaUrl(cardBackPhoto(card).photo_url) : "";
const getAnonymousBinderViewerId = () => {
  const storageKey = "cardora_binder_viewer_id";
  const existing = localStorage.getItem(storageKey);
  if (existing) return existing;
  const generated = window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(storageKey, generated);
  return generated;
};

function PublicBinder({ binderOnly = false }) {
  const { userIdOrHandle, sharedCollectionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [collections, setCollections] = useState([]);
  const [collectionsLoaded, setCollectionsLoaded] = useState(false);
  const [summary, setSummary] = useState({ stats: {}, tournaments: [] });
  const [socialSummary, setSocialSummary] = useState({ followers_count: 0, following_count: 0 });
  const [reviews, setReviews] = useState([]);
  const [posts, setPosts] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState({ status: "anonymous" });
  const [activeProfileTab, setActiveProfileTab] = useState(binderOnly ? "binder" : searchParams.get("tab") || "binder");
  const [sharedBinderFilter, setSharedBinderFilter] = useState(binderOnly ? normalizeSharedBinderFilter(searchParams.get("view")) : "all");
  const [activeCollectionId, setActiveCollectionId] = useState(sharedCollectionId || searchParams.get("collection") || "all");
  const [likedCollectionIds, setLikedCollectionIds] = useState(new Set());
  const [myCards, setMyCards] = useState([]);
  const [myCollections, setMyCollections] = useState([]);
  const [tradeSubmitting, setTradeSubmitting] = useState(false);
  const [sentTradeId, setSentTradeId] = useState(null);
  const [socialAction, setSocialAction] = useState("");
  const [tradeStep, setTradeStep] = useState(1);
  const [selectedCard, setSelectedCard] = useState(null);
  const [binderPreviewCard, setBinderPreviewCard] = useState(null);
  const [binderPreviewSide, setBinderPreviewSide] = useState("front");
  const [isBinderPreviewZoomed, setIsBinderPreviewZoomed] = useState(false);
  const [binderPreviewZoomOrigin, setBinderPreviewZoomOrigin] = useState({ x: 50, y: 50 });
  const [binderPage, setBinderPage] = useState(0);
  const [binderViewMode, setBinderViewMode] = useState("binder");
  const [showCardPreview, setShowCardPreview] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [offeredCardIds, setOfferedCardIds] = useState([]);
  const [tradeSearch, setTradeSearch] = useState("");
  const [tradeBinderFilter, setTradeBinderFilter] = useState("all");
  const [wantedSetFilter, setWantedSetFilter] = useState("");
  const [message, setMessage] = useState("");
  const [privateMessage, setPrivateMessage] = useState({ subject: "", body: "" });
  const [notice, setNotice] = useState("");
  const [expandedCommentPostIds, setExpandedCommentPostIds] = useState(new Set());
  const [postLikes, setPostLikes] = useState([]);
  const [showPostLikes, setShowPostLikes] = useState(false);
  const [postLikesLoading, setPostLikesLoading] = useState(false);
  const [pendingLikedCardIds, setPendingLikedCardIds] = useState(new Set());
  const [wantedMatchResult, setWantedMatchResult] = useState(null);
  const [cardEventContext, setCardEventContext] = useState({ loading: false, sharedEvents: [], suggestedEvents: [] });

  useEffect(() => {
    setProfileLoading(true);
    api
      .get(`/binder/users/${userIdOrHandle}`)
      .then(({ data }) => {
        setProfile(data.profile);
        setCards(data.cards || []);
      })
      .catch(() => {
        setProfile(null);
        setCards([]);
      })
      .finally(() => setProfileLoading(false));
    setCollectionsLoaded(false);
    api.get(`/collections/users/${userIdOrHandle}`)
      .then(({ data }) => {
        const nextCollections = data || [];
        setCollections(nextCollections);
        setLikedCollectionIds(new Set(
          nextCollections.filter(collection => collection.viewer_liked).map(collection => collection.id)
        ));
      })
      .catch(() => {
        setCollections([]);
        setLikedCollectionIds(new Set());
      }).finally(() => setCollectionsLoaded(true));
    api.get(`/users/${userIdOrHandle}/summary`).then(({ data }) => setSummary(data)).catch(() => setSummary({ stats: {}, tournaments: [] }));
    api.get(`/users/${userIdOrHandle}/reviews`).then(({ data }) => setReviews(data || [])).catch(() => setReviews([]));
    api.get(`/feed/users/${userIdOrHandle}`).then(({ data }) => setPosts(data.posts || [])).catch(() => setPosts([]));

    if (localStorage.getItem("token")) {
      api.get("/binder/me").then(({ data }) => setMyCards(data)).catch(() => setMyCards([]));
      api.get("/collections/me").then(({ data }) => setMyCollections(data || [])).catch(() => setMyCollections([]));
    }
  }, [userIdOrHandle]);
  useEffect(() => {
    const collectionId = binderOnly ? sharedCollectionId : activeCollectionId;
    if (!collectionsLoaded || !collectionId || collectionId === "all") return;
    const hasToken = Boolean(localStorage.getItem("token"));
    api.post(`/collections/${collectionId}/view`, hasToken ? {} : {
      anonymousSessionId: getAnonymousBinderViewerId()
    }).then(({ data }) => {
      setCollections(current => current.map(collection => collection.id === collectionId
        ? {
            ...collection,
            views_count: data.views_count,
            unique_viewers_count: data.unique_viewers_count
          }
        : collection));
    }).catch(() => {});
  }, [activeCollectionId, binderOnly, collectionsLoaded, sharedCollectionId]);

  useEffect(() => {
    if (!notice) return undefined;
    const noticeTimer = setTimeout(() => setNotice(""), 3200);
    return () => clearTimeout(noticeTimer);
  }, [notice]);

  useEffect(() => {
    if (!profile?.id) return;
    api.get(`/social/users/${profile.id}/summary`).then(({ data }) => setSocialSummary(data)).catch(() => setSocialSummary({ followers_count: 0, following_count: 0 }));
    if (!localStorage.getItem("token")) return;
    api.get(`/social/users/${profile.id}/status`).then(({ data }) => setConnectionStatus(data)).catch(() => setConnectionStatus({ status: "none" }));
  }, [profile?.id]);

  useEffect(() => {
    if (binderOnly) {
      setActiveProfileTab("binder");
      setSharedBinderFilter(normalizeSharedBinderFilter(searchParams.get("view")));
      setActiveCollectionId(sharedCollectionId || "all");
      return;
    }
    setActiveProfileTab(searchParams.get("tab") || "binder");
    setSharedBinderFilter(normalizeSharedBinderFilter(searchParams.get("view")));
    setActiveCollectionId(searchParams.get("collection") || "all");
  }, [binderOnly, searchParams, sharedCollectionId]);

  const isConnectedWithProfile = connectionStatus.status === "connected";
  const isPendingOutgoingConnection = connectionStatus.status === "pending_outgoing";
  const isPendingIncomingConnection = connectionStatus.status === "pending_incoming";

  const canBuildTrade = card => Boolean(card && (card.status === "wanted" || isTradeableCard(card)));

  const registerForCard = (card, intent = "interest") => {
    const params = new URLSearchParams(searchParams);
    params.set("card", String(card.id || card.catalog_card_id || card.card_id));
    params.set("intent", intent);
    if (card.collection_id) params.set("collection", card.collection_id);
    const path = window.location.pathname + "?" + params.toString();
    return "/register?returnTo=" + encodeURIComponent(path);
  };

  const cardPrimaryLabel = card => {
    if (isTradeableCard(card)) return "Proponer intercambio";
    const isAvailable = card?.status === "for_trade" || card?.status === "for_sale" || card?.card_intention === "for_trade";
    if (isConnectedWithProfile) return isAvailable ? "Proponer intercambio" : "Preguntar por la carta";
    if (isPendingIncomingConnection) return "Aceptar conexion";
    if (isPendingOutgoingConnection) return "Solicitud enviada";
    return isAvailable ? "Conectar para intercambiar" : "Conectar por esta carta";
  };
  const formatEventDate = startsAt => startsAt
    ? new Date(startsAt).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "Fecha por confirmar";

  const eventDetailPath = event => event.event_type === "trade_day"
    ? `/store/${event.business_slug}?tab=tradeDays`
    : `/tournaments/${event.id}`;

  const loadCardEventContext = async () => {
    if (!localStorage.getItem("token") || !profile?.id || connectionStatus.status === "self") {
      setCardEventContext({ loading: false, sharedEvents: [], suggestedEvents: [] });
      return;
    }
    setCardEventContext({ loading: true, sharedEvents: [], suggestedEvents: [] });
    try {
      const { data } = await api.get(`/social/users/${profile.id}/shared-events`);
      setCardEventContext({
        loading: false,
        sharedEvents: data.sharedEvents || [],
        suggestedEvents: data.suggestedEvents || []
      });
    } catch {
      setCardEventContext({ loading: false, sharedEvents: [], suggestedEvents: [] });
    }
  };

  const openTradeModal = card => {
    setSelectedCard(card);
    setTradeStep(1);
    setOfferedCardIds([]);
    setTradeSearch("");
    setTradeBinderFilter("all");
    setMessage(isConnectedWithProfile
      ? `Me interesa ${card.name}. Si coincidimos en algun evento, podemos hablarlo alli.`
      : `Me interesa conectar por ${card.name}. Podemos verlo en un evento o acordar un punto.`
    );
    setNotice("");
    loadCardEventContext();
  };

  const handleCardPrimaryAction = card => {
    if (isTradeableCard(card)) { openTradeModal(card); return; }
    if (isConnectedWithProfile) {
      openTradeModal(card);
      return;
    }
    if (isPendingIncomingConnection) {
      acceptConnection();
      return;
    }
    if (isPendingOutgoingConnection) {
      setNotice("La solicitud de conexion ya esta enviada.");
      return;
    }
    openTradeModal(card);
  };

  const proposeTrade = async event => {
    event.preventDefault();
    if (tradeSubmitting) return;
    setNotice("");
    setTradeSubmitting(true);
    try {
      const formalTrade = canBuildTrade(selectedCard);
      if (!formalTrade) {
        if (!selectedCard.viewer_liked) await toggleCardLike(selectedCard);
        setSelectedCard(null);
        setNotice(selectedCard.viewer_liked ? "Ya habias avisado interes por esta carta." : "Aviso de interes enviado.");
        return;
      }
      if (selectedCard?.status === "wanted" && offeredCardIds.length === 0) {
        setNotice("Selecciona al menos una carta tuya para ofrecer.");
        return;
      }
      const { data } = await api.post("/trades", {
        receiverId: profile.id,
        operationType: "trade",
        cashAmountCents: null,
        currency: "EUR",
        offeredUserCardIds: offeredCardIds,
        requestedUserCardIds: selectedCard && selectedCard.status !== "wanted" ? [selectedCard.id] : [],
        message
      });
      setSelectedCard(null);
      setNotice("Propuesta enviada. Puedes seguirla desde Intereses.");
      setSentTradeId(data.id);
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo enviar la propuesta.");
    } finally {
      setTradeSubmitting(false);
    }
  };

  const updateCardLikeState = (cardId, patch) => {
    setCards(current => current.map(card => card.id === cardId ? { ...card, ...patch } : card));
    setBinderPreviewCard(current => current?.id === cardId ? { ...current, ...patch } : current);
    setSelectedCard(current => current?.id === cardId ? { ...current, ...patch } : current);
  };

  const handleInlineCardLike = (event, card) => {
    event.preventDefault();
    event.stopPropagation();
    toggleCardLike(card);
  };

  const toggleCardLike = async card => {
    if (pendingLikedCardIds.has(card.id)) return;
    if (!localStorage.getItem("token")) {
      setNotice("Registrate o inicia sesion para marcar interes en cartas.");
      return;
    }
    if (connectionStatus.status === "self") {
      setNotice("Esta carta es tuya.");
      return;
    }

    setPendingLikedCardIds(current => new Set(current).add(card.id));

    const wasLiked = Boolean(card.viewer_liked);
    const nextCount = Math.max(0, Number(card.likes_count || 0) + (wasLiked ? -1 : 1));
    updateCardLikeState(card.id, { viewer_liked: !wasLiked, likes_count: nextCount });

    try {
      const { data } = wasLiked
        ? await api.delete(`/binder/cards/${card.id}/like`)
        : await api.post(`/binder/cards/${card.id}/like`);
      updateCardLikeState(card.id, {
        viewer_liked: Boolean(data.liked),
        likes_count: Number(data.likes_count || 0)
      });
      if (!wasLiked) setNotice("Marcaste que te gusta esta carta.");
    } catch {
      updateCardLikeState(card.id, { viewer_liked: wasLiked, likes_count: Number(card.likes_count || 0) });
      setNotice("No pudimos actualizar el like de esta carta.");
    } finally {
      setPendingLikedCardIds(current => {
        const next = new Set(current);
        next.delete(card.id);
        return next;
      });
    }
  };

  const markCardAsWanted = async card => {
    if (!localStorage.getItem("token")) {
      setNotice("Registrate o inicia sesion para guardar esta carta como buscada.");
      return;
    }
    if (connectionStatus.status === "self") {
      setNotice("Esta carta ya esta en tu binder.");
      return;
    }

    try {
      const { data } = await api.post(`/binder/cards/${card.id}/want`);
      const matches = Number(data.matchEvaluation?.matches || 0);
      setWantedMatchResult({
        cardId: card.id,
        created: Boolean(data.created),
        matches,
        evaluationOk: data.matchEvaluation?.ok !== false,
        error: data.matchEvaluation?.error
      });
      setNotice(data.created
        ? matches > 0
          ? `Carta guardada. Encontramos ${matches} coincidencia${matches === 1 ? "" : "s"}.`
          : "Carta guardada en tus buscadas. Te avisaremos cuando aparezca una coincidencia."
        : "Esta carta ya estaba en tus buscadas.");
    } catch (error) {
      setNotice(error?.response?.data?.error || "No pudimos marcar esta carta como buscada.");
    }
  };

  const likeCollection = async collectionId => {
    if (likedCollectionIds.has(collectionId)) {
      await api.delete(`/collections/${collectionId}/like`);
      setLikedCollectionIds(current => {
        const next = new Set(current);
        next.delete(collectionId);
        return next;
      });
      setCollections(current => current.map(collection => (
        collection.id === collectionId
          ? { ...collection, likes_count: Math.max(0, (collection.likes_count || 0) - 1) }
          : collection
      )));
      return;
    }

    await api.post(`/collections/${collectionId}/like`);
    setLikedCollectionIds(current => new Set(current).add(collectionId));
    setCollections(current => current.map(collection => (
      collection.id === collectionId
        ? { ...collection, likes_count: (collection.likes_count || 0) + 1 }
        : collection
    )));
  };

  const togglePostLike = async post => {
    if (!localStorage.getItem("token")) {
      setNotice("Necesitas iniciar sesion para dar like.");
      return;
    }
    const wasLiked = Boolean(post.liked_by_me);
    setPosts(current => current.map(item => item.id === post.id ? {
      ...item,
      liked_by_me: !wasLiked,
      likes_count: Math.max(0, Number(item.likes_count || 0) + (wasLiked ? -1 : 1))
    } : item));
    try {
      if (wasLiked) await api.delete(`/feed/${post.id}/like`);
      else await api.post(`/feed/${post.id}/like`);
    } catch {
      api.get(`/feed/users/${userIdOrHandle}`).then(({ data }) => setPosts(data.posts || [])).catch(() => {});
    }
  };

  const togglePostComments = postId => {
    setExpandedCommentPostIds(current => {
      const next = new Set(current);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };

  const openPostLikes = async postId => {
    setPostLikes([]);
    setShowPostLikes(true);
    setPostLikesLoading(true);
    try {
      const { data } = await api.get(`/feed/${postId}/likes`);
      setPostLikes(data || []);
    } catch {
      setNotice("No se pudo cargar quien dio like.");
      setShowPostLikes(false);
    } finally {
      setPostLikesLoading(false);
    }
  };

  const connectUser = async () => {
    const { data } = await api.post(`/social/users/${profile.id}/follow`);
    setConnectionStatus(data?.status ? { status: data.status, requestId: data.id } : { status: "pending_outgoing" });
    setNotice(data?.status === "accepted" ? "Ya estais conectados." : "Solicitud enviada.");
  };

  const acceptConnection = async () => {
    if (!connectionStatus.requestId) return;
    await api.post(`/social/connection-requests/${connectionStatus.requestId}/accept`);
    setConnectionStatus({ status: "connected" });
    setNotice("Conexion aceptada.");
  };

  const blockUser = async () => {
    setSocialAction("block");
    try {
      await api.post("/social/users/" + profile.id + "/block");
      setConnectionStatus({ status: "blocked_by_me" });
      setNotice("Usuario bloqueado.");
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo bloquear al usuario.");
    } finally {
      setSocialAction("");
    }
  };

  const unblockUser = async () => {
    setSocialAction("unblock");
    try {
      await api.delete("/social/users/" + profile.id + "/block");
      setConnectionStatus({ status: "none" });
      setNotice("Usuario desbloqueado. Puedes volver a conectar si quieres.");
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo desbloquear al usuario.");
    } finally {
      setSocialAction("");
    }
  };

  const sendPrivateMessage = async event => {
    event.preventDefault();
    await api.post("/messages", {
      receiverId: profile.id,
      subject: privateMessage.subject,
      body: privateMessage.body
    });
    setNotice("Mensaje enviado.");
  };

  const updatePublicView = ({ tab = activeProfileTab, collectionId = activeCollectionId }) => {
    const nextParams = {};
    if (tab && tab !== "binder") nextParams.tab = tab;
    if (collectionId && collectionId !== "all") nextParams.collection = collectionId;
    setSearchParams(nextParams);
  };

  const copyShareLink = async (tab = activeProfileTab, collectionId = activeCollectionId) => {
    const nextParams = new URLSearchParams();
    if (tab && tab !== "binder") nextParams.set("tab", tab);
    if (collectionId && collectionId !== "all") nextParams.set("collection", collectionId);
    const query = nextParams.toString();
    const profileKey = profile?.handle || profile?.id || userIdOrHandle;
    const url = tab === "binder" && collectionId && collectionId !== "all"
      ? `${window.location.origin}/binder/${profileKey}/${collectionId}`
      : `${window.location.origin}/u/${profileKey}?${query}`;
    const ownerName = profile?.display_name || profile?.handle || "Coleccionista";
    const selectedCollection = collections.find(collection => collection.id === collectionId);
    const binderName = selectedCollection?.name || "Todos los binders";
    const title = tab === "trade"
      ? `Cartas para trade de ${ownerName}`
      : tab === "wanted"
        ? `Cartas buscadas por ${ownerName}`
        : `${binderName} de ${ownerName}`;
    const text = `${title} en Cardora`;

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        setNotice("Compartido.");
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    await navigator.clipboard.writeText(url);
    setNotice("Link copiado.");
  };

  const visibleCards = activeCollectionId === "all"
    ? cards
    : cards.filter(card => card.collection_id === activeCollectionId);
  const highlightedCardId = searchParams.get("card");
  const activeCollection = collections.find(collection => collection.id === activeCollectionId);
  const stats = summary.stats || {};
  const tradeCards = visibleCards.filter(isTradeableCard);
  const tradeOnlyQuantity = sharedBinderFilter === "trade" || activeProfileTab === "trade";
  const wantedCards = visibleCards.filter(card => card.status === "wanted");
  const missingPublicCards = Array.from(new Map(visibleCards.filter(card => card.is_missing || card.status === "wanted")
    .map(card => [card.catalog_card_id || card.card_id || card.id, card])).values());
  const availablePublicCards = visibleCards.filter(isTradeableCard);
  const duplicateCards = visibleCards.filter(card => card.status !== "wanted" && (Number(card.quantity) > 1 || Number(card.available_quantity) > 0));
  const wantedSetNames = [...new Set(wantedCards.map(card => String(card.set_name || "").trim()).filter(Boolean))]
    .sort((left, right) => left.localeCompare(right, "es", { sensitivity: "base" }));
  const isWantedByProfile = card => wantedCards.some(wanted => {
    if (card.card_id && wanted.card_id) return card.card_id === wanted.card_id;
    return String(card.name || "").trim().toLowerCase() === String(wanted.name || "").trim().toLowerCase()
      && (!card.collector_number || !wanted.collector_number || String(card.collector_number) === String(wanted.collector_number))
      && (!card.set_name || !wanted.set_name || String(card.set_name).trim().toLowerCase() === String(wanted.set_name).trim().toLowerCase());
  });
  const filteredWantedCards = wantedSetFilter
    ? wantedCards.filter(card => card.set_name === wantedSetFilter)
    : wantedCards;
  const rawBinderCards = activeCollection?.binder_intent === "trade"
    ? visibleCards.filter(card => ["for_trade", "for_sale"].includes(card.status) && card.availability !== "unavailable")
    : activeCollection?.binder_structure === "set"
      ? visibleCards
      : visibleCards.filter(card => card.status !== "wanted" && !card.is_missing);
  const orderedBinderCards = activeCollection?.binder_structure === "set"
    ? [...rawBinderCards].sort((left, right) => new Intl.Collator("es", { numeric: true }).compare(String(left.collector_number || ""), String(right.collector_number || "")))
    : rawBinderCards;
  const binderCards = sharedBinderFilter === "all"
    ? orderedBinderCards
    : sharedBinderFilter === "duplicates"
      ? orderedBinderCards.filter(card => card.status !== "wanted" && (Number(card.quantity) > 1 || Number(card.available_quantity) > 0))
      : sharedBinderFilter === "trade"
        ? orderedBinderCards.filter(isTradeableCard)
        : sharedBinderFilter === "owned" ? orderedBinderCards.filter(card => !card.is_missing && card.status !== "wanted")
        : [...(sharedBinderFilter === "wanted" ? wantedCards : missingPublicCards)].sort((left, right) => new Intl.Collator("es", { numeric: true }).compare(String(left.collector_number || ""), String(right.collector_number || "")));
  const getPublicSetProgress = collection => {
    if (collection?.binder_structure !== "set") return null;
    const setCards = cards.filter(card => card.collection_id === collection.id);
    const slots = new Map();
    const completed = new Set();
    setCards.forEach(card => {
      const catalogCardId = card.catalog_card_id || card.card_id;
      if (!catalogCardId) return;
      slots.set(catalogCardId, true);
      if (card.id && card.status !== "wanted" && !card.is_missing) completed.add(catalogCardId);
    });
    return {
      completed: completed.size,
      total: slots.size,
      percentage: slots.size ? Math.round((completed.size / slots.size) * 100) : 0
    };
  };
  const publicSetProgressById = new Map(collections.map(collection => [collection.id, getPublicSetProgress(collection)]));
  const activeSetProgress = publicSetProgressById.get(activeCollectionId) || null;
  const publicCardKey = card => card?.id || card?.catalog_card_id || card?.card_id || card?.external_id || [card?.collection_id, card?.set_name, card?.collector_number, card?.name].filter(Boolean).join(":");
  const binderPageCount = Math.max(1, Math.ceil(binderCards.length / BINDER_CARDS_PER_PAGE));
  const binderPageCards = binderCards.slice(
    binderPage * BINDER_CARDS_PER_PAGE,
    binderPage * BINDER_CARDS_PER_PAGE + BINDER_CARDS_PER_PAGE
  );
  const previewCards = activeProfileTab === "trade" ? tradeCards : activeProfileTab === "wanted" ? wantedCards : activeProfileTab === "duplicates" ? duplicateCards : binderCards;
  const activeBinderCard = binderPreviewCard && previewCards.some(card => publicCardKey(card) === publicCardKey(binderPreviewCard))
    ? binderPreviewCard
    : activeProfileTab === "binder" ? binderPageCards[0] || null : previewCards[0] || null;

  useEffect(() => {
    if (!profile) return undefined;
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content") || "";
    const ownerName = profile.display_name || profile.handle || "Coleccionista";
    const binderName = activeCollection?.name || "Binders";
    document.title = `${binderName} de ${ownerName} | Cardora`;
    if (description) description.setAttribute("content", `Explora ${binderName}, el binder de ${ownerName} en Cardora.`);
    return () => {
      document.title = previousTitle;
      if (description) description.setAttribute("content", previousDescription);
    };
  }, [activeCollection, profile]);

  useEffect(() => {
    setBinderPage(0);
    setBinderPreviewCard(null);
    setBinderPreviewSide("front");
  }, [activeCollectionId, sharedBinderFilter]);

  const updateSharedBinderFilter = filter => {
    const normalizedFilter = normalizeSharedBinderFilter(filter);
    setSharedBinderFilter(normalizedFilter);
    setBinderPage(0);
    setBinderPreviewCard(null);
    setBinderPreviewSide("front");
    const nextParams = new URLSearchParams(searchParams);
    if (normalizedFilter === "all") nextParams.delete("view");
    else nextParams.set("view", normalizedFilter);
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    if (binderPage >= binderPageCount) setBinderPage(Math.max(0, binderPageCount - 1));
  }, [binderPage, binderPageCount]);
  const movePublicBinderPage = direction => {
    const nextPage = Math.min(Math.max(binderPage + direction, 0), binderPageCount - 1);
    const nextCard = binderCards[nextPage * BINDER_CARDS_PER_PAGE] || null;
    setBinderPage(nextPage);
    setBinderPreviewCard(nextCard);
    setBinderPreviewSide("front");
    setIsBinderPreviewZoomed(false);
    setBinderPreviewZoomOrigin({ x: 50, y: 50 });
  };

  useEffect(() => {
    if (!["binder", "wanted", "trade", "duplicates"].includes(activeProfileTab) || !highlightedCardId || cards.length === 0) return;
    const scopedCards = activeCollectionId === "all"
      ? cards
      : cards.filter(card => card.collection_id === activeCollectionId);
    const scopedBinderCards = activeProfileTab === "wanted"
      ? scopedCards.filter(card => card.status === "wanted")
      : activeProfileTab === "trade"
        ? scopedCards.filter(isTradeableCard)
        : activeProfileTab === "duplicates"
          ? scopedCards.filter(card => card.status !== "wanted" && (Number(card.quantity) > 1 || Number(card.available_quantity) > 0))
          : scopedCards;
    const highlightedIndex = scopedBinderCards.findIndex(card => String(card.id || card.catalog_card_id || card.card_id) === highlightedCardId);
    if (highlightedIndex < 0) return;

    const highlightedCard = scopedBinderCards[highlightedIndex];
    if (activeProfileTab === "binder") setBinderPage(Math.floor(highlightedIndex / BINDER_CARDS_PER_PAGE));
    setBinderPreviewCard(highlightedCard);
    setBinderPreviewSide("front");
    setShowCardPreview(true);
  }, [activeProfileTab, highlightedCardId, activeCollectionId, cards]);

  const closeCardPreview = () => {
    setShowCardPreview(false);
    setWantedMatchResult(null);
    setBinderPreviewSide("front");
    setIsBinderPreviewZoomed(false);
    setBinderPreviewZoomOrigin({ x: 50, y: 50 });
  };

  const openActionFromPreview = action => {
    if (!activeBinderCard) return;
    closeCardPreview();
    action(activeBinderCard);
  };

  const selectBinderCard = card => {
    setWantedMatchResult(null);
    setBinderPreviewCard(card);
    setBinderPreviewSide("front");
    setIsBinderPreviewZoomed(false);
    setBinderPreviewZoomOrigin({ x: 50, y: 50 });
    setShowCardPreview(true);
  };


  const movePublicBinderCard = delta => {
    if (!activeBinderCard || previewCards.length === 0) return;
    const currentIndex = previewCards.findIndex(card => publicCardKey(card) === publicCardKey(activeBinderCard));
    const nextIndex = (Math.max(0, currentIndex) + delta + previewCards.length) % previewCards.length;
    const nextCard = previewCards[nextIndex];
    setBinderPreviewCard(nextCard);
    if (activeProfileTab === "binder") setBinderPage(Math.floor(nextIndex / BINDER_CARDS_PER_PAGE));
    setBinderPreviewSide("front");
    setIsBinderPreviewZoomed(false);
    setBinderPreviewZoomOrigin({ x: 50, y: 50 });
  };

  const rotatePublicCard = () => {
    if (!cardBackImage(activeBinderCard)) return;
    setBinderPreviewSide(current => current === "front" ? "back" : "front");
  };

  const moveBinderPreviewZoom = event => {
    if (!isBinderPreviewZoomed) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    setBinderPreviewZoomOrigin({
      x: Math.max(0, Math.min(100, ((event.clientX - bounds.left) / bounds.width) * 100)),
      y: Math.max(0, Math.min(100, ((event.clientY - bounds.top) / bounds.height) * 100))
    });
  };

  const resetBinderPreviewZoom = () => {
    setIsBinderPreviewZoomed(false);
    setBinderPreviewZoomOrigin({ x: 50, y: 50 });
  };

  const openWantedOfferModal = card => {
    openTradeModal(card);
    setTradeStep(2);
    setOfferedCardIds([]);
    setMessage(`Tengo una carta que buscas: ${card.name}. Te interesa verla?`);
    setNotice("");
  };

  const renderCardGrid = (cardList, emptyText, mode = "binder") => (
    <Row className="g-3">
      {cardList.length === 0 && (
        <Col>
          <Card className="cardora-card">
            <Card.Body>{emptyText}</Card.Body>
          </Card>
        </Col>
      )}
      {cardList.map(card => (
        <Col sm={6} lg={4} xl={3} key={publicCardKey(card)}>
          <Card className="cardora-card public-binder-card h-100">
            <Card.Body>
              <button type="button" className="public-trade-card-image" onClick={() => selectBinderCard(card)} aria-label={`Ver frente y reverso de ${card.name}`}>
                <img src={cardFrontImage(card)} alt={card.name} loading="lazy" decoding="async" />
                <span><i className="bi bi-arrow-repeat" /> {cardBackImage(card) ? "Ver frente y reverso" : "Ver carta"}</span>
              </button>
              <div className="d-flex justify-content-between gap-2 align-items-start mt-3">
                <strong>{card.name}</strong>
                <Badge bg={card.status === "wanted" ? "secondary" : card.status === "for_trade" ? "warning" : "success"} text={card.status === "for_trade" ? "dark" : undefined}>
                  {intentionLabels[card.card_intention] || statusLabels[card.status] || card.status}
                </Badge>
              </div>
              <div className="muted-copy">{card.set_name} - {card.collector_number}</div>
              {!card.is_missing && card.status !== "wanted" && <small>{binderQuantityLabel(card, activeProfileTab === "trade")}</small>}
              {mode === "wanted" && localStorage.getItem("token") && connectionStatus.status !== "self" && (
                <Button className="btn-cardora w-100 mt-3" onClick={() => openWantedOfferModal(card)}>
                  Tengo una carta que buscas
                </Button>
              )}
              {mode !== "wanted" && ["for_trade", "for_sale", "showcase"].includes(card.status) && localStorage.getItem("token") && connectionStatus.status !== "self" && (
                <Button
                  className="btn-cardora w-100 mt-3"
                  disabled={isPendingOutgoingConnection && !isTradeableCard(card)}
                  onClick={() => handleCardPrimaryAction(card)}
                >
                  {cardPrimaryLabel(card)}
                </Button>
              )}
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const profileTabMenu = !binderOnly && (
    <Nav
      variant="tabs"
      activeKey={activeProfileTab}
      onSelect={key => updatePublicView({ tab: key || "posts", collectionId: activeCollectionId })}
      className="binder-tabs mb-4"
    >
      <Nav.Item><Nav.Link eventKey="binder"><i className="bi bi-journal-album me-2" />Binders</Nav.Link></Nav.Item>
      <Nav.Item><Nav.Link eventKey="posts"><i className="bi bi-chat-square-text me-2" />Actividad</Nav.Link></Nav.Item>
      <Nav.Item><Nav.Link eventKey="trade"><i className="bi bi-arrow-left-right me-2" />Trade</Nav.Link></Nav.Item>
      <Nav.Item><Nav.Link eventKey="duplicates"><i className="bi bi-copy me-2" />Copias</Nav.Link></Nav.Item>
      <Nav.Item><Nav.Link eventKey="wanted"><i className="bi bi-search me-2" />Buscadas</Nav.Link></Nav.Item>
      <Nav.Item><Nav.Link eventKey="tournaments"><i className="bi bi-trophy me-2" />Eventos</Nav.Link></Nav.Item>
      <Nav.Item><Nav.Link eventKey="reviews"><i className="bi bi-star me-2" />Valoraciones</Nav.Link></Nav.Item>
    </Nav>
  );

  useFeedbackMessage(notice, notice.startsWith("No") ? "danger" : "success");

  if (profileLoading) return <Container className="page-band profile-page"><ProfileSkeleton compact={binderOnly} /></Container>;

  return (
    <Container className="page-band profile-page">
      <div className={`profile-hero mb-4 ${binderOnly ? "shared-binder-hero" : ""}`}>
        <div className="profile-avatar">
          {profile?.avatar_url ? <img src={mediaUrl(profile.avatar_url)} alt="" /> : <span>{profile?.display_name?.slice(0, 1) || "C"}</span>}
        </div>
        {binderOnly ? (
          <>
            <div className="shared-binder-profile-info">
              <h1 className="section-title mb-0">{activeCollection?.name || "Binder compartido"}</h1>
              <span>de {profile?.display_name || profile?.handle || "Coleccionista"}{activeCollection ? <> · <i className="bi bi-eye" /> {activeCollection.views_count || 0} vistas</> : ""}</span>
            </div>
            <Button as={Link} to={`/u/${profile?.handle || profile?.id || userIdOrHandle}`} variant="outline-dark">
              <i className="bi bi-person me-2" />Ver perfil
            </Button>
          </>
        ) : <div className="public-profile-identity">
          <h1 className="section-title mb-1">{profile?.display_name || "Binder publico"}</h1>
          <p className="muted-copy mb-1">{profile?.bio || "Coleccionista Cardora"}</p>
          {profile?.location_text && <p className="muted-copy small mb-2"><i className="bi bi-geo-alt me-1" />{profile.location_text}</p>}
          {profile?.handle && <span className="public-profile-handle">@{profile.handle}</span>}
        </div>}
        {!binderOnly && !localStorage.getItem("token") && profile && (
          <div className="profile-actions">
            <Button as={Link} to="/register" className="btn-cardora">Registrarme para conectar</Button>
            <Button as={Link} to="/register" variant="outline-dark">Registrarme para ofertar</Button>
          </div>
        )}
        {!binderOnly && localStorage.getItem("token") && profile && connectionStatus.status !== "self" && (
          <div className="profile-actions">
            {connectionStatus.status === "none" && <Button variant="outline-dark" onClick={connectUser}>Conectar</Button>}
            {connectionStatus.status === "pending_outgoing" && <Button variant="outline-secondary" disabled>Solicitud enviada</Button>}
            {connectionStatus.status === "pending_incoming" && <Button variant="outline-dark" onClick={acceptConnection}>Aceptar conexion</Button>}
            {connectionStatus.status === "connected" && <Button variant="outline-success" disabled>Conectados</Button>}
            {connectionStatus.status === "blocked_by_me" && <Button variant="outline-dark" disabled={socialAction === "unblock"} onClick={unblockUser}>{socialAction === "unblock" && <Spinner size="sm" className="me-2" />}Desbloquear</Button>}
            {!["blocked", "blocked_by_me"].includes(connectionStatus.status) && (
              <Button variant="outline-dark" onClick={() => setShowMessageModal(true)}>Mensaje</Button>
            )}
            {!["blocked", "blocked_by_me"].includes(connectionStatus.status) && (
              <Button variant="outline-danger" disabled={socialAction === "block"} onClick={blockUser}>{socialAction === "block" && <Spinner size="sm" className="me-2" />}Bloquear</Button>
            )}
            {activeProfileTab === "binder" && activeCollection && (
              <Button variant="outline-dark" onClick={() => likeCollection(activeCollection.id)}>
                <i className="bi bi-heart me-2" />
                {likedCollectionIds.has(activeCollection.id) ? "Quitar like" : "Like"} {activeCollection.likes_count || 0}
              </Button>
            )}
          </div>
        )}
      </div>

      {sentTradeId && <div className="shared-binder-sent" role="status"><span><i className="bi bi-check-circle-fill" /> Propuesta enviada</span><Link to={"/trades?trade=" + sentTradeId}>Seguir en Intereses <i className="bi bi-arrow-right" /></Link></div>}
      {profileTabMenu}

      {activeProfileTab === "posts" && (
        <div className="d-grid gap-3">
          {posts.length === 0 && (
            <Card className="cardora-card">
              <Card.Body>Este perfil aun no tiene publicaciones.</Card.Body>
            </Card>
          )}
          {posts.map(post => (
            <Card className={`cardora-card feed-card ${postTypeClass(post.type)}`} key={post.id}>
              <Card.Body>
                <div className="feed-post-head">
                  <Link className="feed-avatar" to={`/u/${post.handle || post.user_id}`}>
                    {post.avatar_url ? <img src={mediaUrl(post.avatar_url)} alt="" /> : <span>{post.display_name?.trim()?.slice(0, 1).toUpperCase() || "C"}</span>}
                  </Link>
                  <div className="feed-post-meta">
                    <div className="feed-post-author-line">
                      <Link className="feed-author" to={`/u/${post.handle || post.user_id}`}>{post.display_name}</Link>
                    </div>
                    <time>{new Date(post.created_at).toLocaleString("es-ES")}</time>
                  </div>
                  <div className="feed-post-head-actions">
                    <Badge className={`feed-type-badge ${postTypeClass(post.type)}`}>{postTypes[post.type] || "Estado"}</Badge>
                  </div>
                </div>
                <p className="feed-post-body">{post.body}</p>
                {post.images?.length > 0 && (
                  <div className={`feed-image-grid ${post.images.length === 1 ? "single" : ""}`}>
                    {[...post.images].sort((a, b) => a.sort_order - b.sort_order).map(image => (
                      <div className="feed-public-image" key={image.id}>
                        <img src={mediaUrl(image.image_url)} alt={`Publicacion de ${post.display_name}`} loading="lazy" decoding="async" />
                      </div>
                    ))}
                  </div>
                )}
                <div className="feed-actions public-profile-feed-actions">
                  <div className="feed-action-group">
                    <div className="d-flex align-items-center gap-1">
                      <Button
                        className="feed-action-button"
                        variant={post.liked_by_me ? "dark" : "outline-dark"}
                        size="sm"
                        onClick={() => togglePostLike(post)}
                        aria-label={post.liked_by_me ? "Quitar Me gusta" : "Dar Me gusta"}
                      >
                        <i className={`bi ${post.liked_by_me ? "bi-heart-fill" : "bi-heart"}`} />
                      </Button>
                      <Button variant="link" size="sm" className="feed-action-count" onClick={() => openPostLikes(post.id)} disabled={!post.likes_count}>
                        {post.likes_count || 0}
                      </Button>
                    </div>
                    <Button variant="outline-dark" size="sm" className="feed-action-button" onClick={() => togglePostComments(post.id)} disabled={!post.comments_count}>
                      <i className="bi bi-chat me-1" />{post.comments_count || 0}
                    </Button>
                  </div>
                </div>
                {expandedCommentPostIds.has(post.id) && (
                  <div className="feed-comments">
                    {[...(post.comments || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map(comment => (
                      <div className="profile-comment-row" key={comment.id}>
                        <Link to={`/u/${comment.handle || comment.user_id}`} className="profile-comment-avatar">
                          {comment.avatar_url ? <img src={mediaUrl(comment.avatar_url)} alt="" /> : <span>{comment.display_name?.slice(0, 1) || "C"}</span>}
                        </Link>
                        <div className="profile-comment-copy">
                          <div className="d-flex justify-content-between gap-2">
                            <Link to={`/u/${comment.handle || comment.user_id}`}><strong>{comment.display_name}</strong></Link>
                            <time>{new Date(comment.created_at).toLocaleString("es-ES")}</time>
                          </div>
                          <span>{comment.body}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {activeProfileTab === "binder" && (
        <>
          {!binderOnly && <section className="binder-selector-bar mb-4" aria-label="Seleccionar binder publico">
            <div className="binder-selector-control">
              <i className="bi bi-journal-album" aria-hidden="true" />
              <Form.Group controlId="public-binder-select">
                <Form.Label>Binder</Form.Label>
                <Form.Select
                  value={activeCollectionId}
                  onChange={event => updatePublicView({ tab: "binder", collectionId: event.target.value })}
                >
                  <option value="all">Todos los binders ({binderCards.length})</option>
                  {collections.map(collection => (
                    <option key={collection.id} value={collection.id}>
                      {collection.name} ({publicSetProgressById.get(collection.id)
                        ? `${publicSetProgressById.get(collection.id).completed}/${publicSetProgressById.get(collection.id).total}`
                        : collection.cards_count || 0})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="binder-selector-summary">
              <strong>{activeCollection?.name || `Binders de ${profile?.display_name || profile?.handle || "coleccionista"}`}</strong>
              <span>{activeSetProgress
                ? `${activeSetProgress.completed}/${activeSetProgress.total} cartas - ${activeSetProgress.percentage}% completado`
                : activeCollection
                  ? `${activeCollection.likes_count || 0} likes${binderThemeText(activeCollection) ? ` - ${binderThemeText(activeCollection)}` : ""}`
                  : "Coleccion publica"}</span>
              {activeSetProgress && <div className="public-binder-progress" aria-label={`${activeSetProgress.percentage}% completado`}>
                <span style={{ width: `${activeSetProgress.percentage}%` }} />
              </div>}
              {activeCollection && <div className="public-binder-engagement"><span><i className="bi bi-eye" />{activeCollection.views_count || 0} vistas</span><span><i className="bi bi-heart" />{activeCollection.likes_count || 0} likes</span></div>}
            </div>
          </section>}
          {(binderOnly || activeCollection) && connectionStatus.status !== "self" && <section className="shared-binder-actions" aria-label="Intercambiar con este coleccionista">
            <p>A {profile?.display_name || "este coleccionista"} le faltan <strong>{missingPublicCards.length} cartas</strong> y tiene <strong>{availablePublicCards.length} disponibles para cambiar</strong>.</p>
            <div>
              <Button variant="outline-dark" disabled={!wantedCards.length} onClick={() => updateSharedBinderFilter("wanted")}><i aria-hidden="true" className="bi bi-check2-circle" />Ver lo que busca</Button>
              <Button className="btn-cardora" disabled={!availablePublicCards.length} onClick={() => updateSharedBinderFilter("trade")}><i aria-hidden="true" className="bi bi-arrow-left-right" />Me interesa una repetida</Button>
            </div>
          </section>}
          <section className="shared-binder-filter-bar mb-3" aria-label="Filtrar cartas del binder">
            {[
              { id: "all", label: "Todo", icon: "bi-grid-3x3-gap", count: orderedBinderCards.length },
              { id: "duplicates", label: "Repetidas", icon: "bi-copy", count: duplicateCards.length },
              { id: "trade", label: "Disponibles", icon: "bi-arrow-left-right", count: tradeCards.length },
              { id: "owned", label: "Tiene", icon: "bi-check-circle", count: orderedBinderCards.filter(card => !card.is_missing && card.status !== "wanted").length },
              { id: "missing", label: "Faltan", icon: "bi-circle", count: missingPublicCards.length },
              { id: "wanted", label: "Busca", icon: "bi-search-heart", count: wantedCards.length }
            ].map(filter => (
              <button key={filter.id} type="button" className={sharedBinderFilter === filter.id ? "is-active" : ""} onClick={() => updateSharedBinderFilter(filter.id)}>
                <i className={`bi ${filter.icon}`} aria-hidden="true" />
                <span>{filter.label}</span>
                <b>{filter.count}</b>
              </button>
            ))}
          </section>
          {activeCollection && localStorage.getItem("token") && connectionStatus.status !== "self" && (
            <Button variant="outline-dark" className="mb-3" onClick={() => likeCollection(activeCollection.id)}>
              <i className="bi bi-heart me-2" />
              {likedCollectionIds.has(activeCollection.id) ? "Quitar like" : "Like"} {activeCollection.likes_count || 0}
            </Button>
          )}
          {binderCards.length === 0 ? (
            <Card className="cardora-card">
              <Card.Body className="cardora-empty-state"><strong>{binderOnly && sharedBinderFilter === "duplicates" ? "Este binder no tiene cartas repetidas." : binderOnly && sharedBinderFilter === "trade" ? "Este binder no tiene cartas disponibles para intercambio." : binderOnly && sharedBinderFilter === "wanted" ? "Este binder no tiene cartas buscadas." : binderOnly && sharedBinderFilter === "missing" ? "No hay cartas faltantes." : "Este binder aun no tiene cartas publicas."}</strong><span>{binderOnly && sharedBinderFilter !== "all" ? "Prueba otro filtro para seguir explorando." : "Explora sus otras secciones o conecta con el coleccionista."}</span>{!binderOnly && <Button type="button" variant="outline-dark" onClick={() => updatePublicView({ tab: "posts", collectionId: activeCollectionId })}>Ver actividad</Button>}</Card.Body>
            </Card>
          ) : (
            <>
              <div className="binder-workspace-header">
                <div>
                  <h2>{activeCollection?.name || "Binder publico"}</h2>
                  {binderThemeText(activeCollection) && <Badge bg="light" text="dark">{binderThemeText(activeCollection)}</Badge>}
                  <span>{activeSetProgress
                    ? `${activeSetProgress.completed}/${activeSetProgress.total} cartas - ${activeSetProgress.percentage}% completado${binderViewMode === "binder" ? ` · pagina ${binderPage + 1}/${binderPageCount}` : ""}`
                    : `${binderCards.length} cartas${binderViewMode === "binder" ? ` · pagina ${binderPage + 1}/${binderPageCount}` : ""}`}</span>
                  {activeSetProgress && <div className="public-binder-progress is-workspace" aria-label={`${activeSetProgress.percentage}% completado`}>
                    <span style={{ width: `${activeSetProgress.percentage}%` }} />
                  </div>}
                  {binderViewMode === "binder" && <small>Selecciona una carta para verla en detalle.</small>}
                </div>
                <div className="binder-view-switch public-binder-view-switch" role="group" aria-label="Vista del binder">
                  <button type="button" className={binderViewMode === "binder" ? "is-active" : ""} onClick={() => setBinderViewMode("binder")} aria-label="Vista binder" title="Vista binder"><i className="bi bi-grid-3x3-gap" /></button>
                  <button type="button" className={binderViewMode === "list" ? "is-active" : ""} onClick={() => setBinderViewMode("list")} aria-label="Vista listado" title="Vista listado"><i className="bi bi-list-ul" /></button>
                </div>
              </div>
              <Row className="g-3 align-items-start binder-workspace">
                <Col xl={4} className="binder-desktop-preview">
                  <section className="binder-feature public-binder-feature">
                    {activeBinderCard && (
                      <>
                        <button
                          type="button"
                          className={`binder-feature-image public-binder-preview-button ${activeBinderCard.is_missing ? "is-missing" : ""}`}
                          onClick={() => setShowCardPreview(true)}
                          aria-label={`Ampliar ${activeBinderCard.name}`}
                        >
                          <img src={cardFrontImage(activeBinderCard)} alt={activeBinderCard.name} />
                          <span className="public-binder-zoom"><i className="bi bi-zoom-in" /></span>
                        </button>
                        <div className="binder-feature-meta">
                          <Badge bg={activeBinderCard.status === "for_trade" ? "warning" : "success"} text={activeBinderCard.status === "for_trade" ? "dark" : undefined}>
                            {intentionLabels[activeBinderCard.card_intention] || statusLabels[activeBinderCard.status] || activeBinderCard.status || "Faltante"}
                          </Badge>
                          <h2>{activeBinderCard.name}</h2>
                          <p>{activeBinderCard.set_name} - {activeBinderCard.collector_number}</p>
                          <div className="binder-stat-row">
                            <RarityMark rarity={activeBinderCard.rarity} />
                            <span>{activeBinderCard.card_type || "Sin tipo"}</span>
                            <span>{activeBinderCard.condition || "Sin estado"}</span>
                            {activeBinderCard.id && <span>{binderQuantityLabel(activeBinderCard, tradeOnlyQuantity)}</span>}
                            {!tradeOnlyQuantity && Number(activeBinderCard.available_quantity) > 0 && <span>{activeBinderCard.available_quantity} para intercambio</span>}
                            <span><i className={`bi ${activeBinderCard.viewer_liked ? "bi-heart-fill" : "bi-heart"} me-1`} />{activeBinderCard.likes_count || 0}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </section>
                </Col>
                <Col xl={8}>
                  {binderViewMode === "list" ? (
                    <div className="binder-list-view public-binder-list-view">
                      {binderCards.map(card => (
                        <button type="button" className="binder-list-row public-binder-list-row" key={publicCardKey(card)} onClick={() => selectBinderCard(card)}>
                          <span className="binder-card-image is-loaded"><img src={cardFrontImage(card)} alt={card.name} loading="lazy" decoding="async" /></span>
                          <span className="binder-list-copy">
                            <strong>{card.name}</strong>
                            <small>{[card.set_name, card.collector_number, card.condition].filter(Boolean).join(" · ")}</small>
                          </span>
                          <span className="binder-list-flags">
                            <Badge bg={card.status === "wanted" ? "secondary" : card.status === "for_trade" ? "warning" : "success"} text={card.status === "for_trade" ? "dark" : undefined}>
                              {intentionLabels[card.card_intention] || statusLabels[card.status] || card.status}
                            </Badge>
                            {binderQuantity(card, tradeOnlyQuantity) > (tradeOnlyQuantity ? 0 : 1) && <b title={binderQuantityLabel(card, tradeOnlyQuantity)}>x{binderQuantity(card, tradeOnlyQuantity)}</b>}
                          </span>
                          <i className="bi bi-chevron-right" />
                        </button>
                      ))}
                    </div>
                  ) : (
                  <div className="binder-page-stage public-binder-page-stage">
                    <Button variant="light" className="binder-page-side is-previous" disabled={binderPage === 0} onClick={() => movePublicBinderPage(-1)} aria-label="Pagina anterior" title="Pagina anterior"><i className="bi bi-chevron-left" /></Button>
                    <span className="binder-page-indicator">Hoja {binderPage + 1} de {binderPageCount}</span>
                    <div className="binder-pocket-grid public-binder-pocket-grid">
                    {Array.from({ length: BINDER_CARDS_PER_PAGE }).map((_, index) => {
                      const card = binderPageCards[index];
                      return card ? (
                        <div className="public-card-pocket-wrap" key={"public-pocket-" + publicCardKey(card)}>
                          <button
                            type="button"
                            className={`binder-pocket ${card.is_missing ? "is-missing" : ""} ${publicCardKey(activeBinderCard) === publicCardKey(card) ? "is-active" : ""} ${highlightedCardId === card.id ? "is-highlighted" : ""}`}
                            onClick={() => selectBinderCard(card)}
                          >
                            <img src={cardFrontImage(card)} alt={card.name} loading="lazy" decoding="async" />
                            {binderQuantity(card, tradeOnlyQuantity) > (tradeOnlyQuantity ? 0 : 1) && <span className="binder-pocket-quantity" title={binderQuantityLabel(card, tradeOnlyQuantity)}>x{binderQuantity(card, tradeOnlyQuantity)}</span>}
                            <small>{binderPage * BINDER_CARDS_PER_PAGE + index + 1}</small>
                            <RarityMark rarity={card.rarity} compact />
                          </button>
                          {card.id && !card.is_missing && connectionStatus.status !== "self" && (
                            <button
                              type="button"
                              className={`public-card-like-corner ${card.viewer_liked ? "is-liked" : ""}`}
                              onPointerDown={event => event.stopPropagation()}
                              onMouseDown={event => event.stopPropagation()}
                              onClick={event => handleInlineCardLike(event, card)}
                              disabled={pendingLikedCardIds.has(card.id)}
                              aria-label={card.viewer_liked ? "Quitar interes" : "Marcar interes"}
                            >
                              <i className={`bi ${card.viewer_liked ? "bi-heart-fill" : "bi-heart"}`} />
                              <span>{card.likes_count || 0}</span>
                            </button>
                          )}
                        </div>
                      ) : <div className="binder-pocket is-empty" key={`empty-${binderPage}-${index}`} />;
                    })}
                    </div>
                    <Button variant="light" className="binder-page-side is-next" disabled={binderPage >= binderPageCount - 1} onClick={() => movePublicBinderPage(1)} aria-label="Pagina siguiente" title="Pagina siguiente"><i className="bi bi-chevron-right" /></Button>
                  </div>
                  )}
                </Col>
              </Row>
            </>
          )}
        </>
      )}

      {activeProfileTab === "trade" && renderCardGrid(tradeCards, "Este perfil no tiene cartas disponibles para conectar.", "trade")}
      {activeProfileTab === "duplicates" && renderCardGrid(duplicateCards, "Este binder no tiene copias repetidas publicas.", "trade")}
      {activeProfileTab === "wanted" && (
        <section className="public-wanted-section">
          <div className="public-wanted-toolbar">
            <div>
              <strong>{filteredWantedCards.length} {filteredWantedCards.length === 1 ? "carta buscada" : "cartas buscadas"}</strong>
              <span>Filtra por set para encontrar mas rapido.</span>
            </div>
            <Form.Select
              aria-label="Filtrar cartas buscadas por set"
              value={wantedSetFilter}
              onChange={event => setWantedSetFilter(event.target.value)}
              disabled={!wantedSetNames.length}
            >
              <option value="">Todos los sets ({wantedCards.length})</option>
              {wantedSetNames.map(setName => (
                <option value={setName} key={setName}>
                  {setName} ({wantedCards.filter(card => card.set_name === setName).length})
                </option>
              ))}
            </Form.Select>
          </div>
          {renderCardGrid(filteredWantedCards, wantedSetFilter ? "No hay cartas buscadas de este set." : "Este perfil no tiene cartas buscadas publicas.", "wanted")}
        </section>
      )}

      {activeProfileTab === "tournaments" && (
        <div className="d-grid gap-3">
          {summary.tournaments?.length === 0 && <Card className="cardora-card"><Card.Body>Aun no participo en eventos.</Card.Body></Card>}
          {summary.tournaments?.map(tournament => (
            <Card className="cardora-card" key={tournament.tournament_id}>
              <Card.Body className="d-flex justify-content-between align-items-center gap-3">
                <div>
                  <strong>{tournament.tournament_name}</strong>
                  <div className="muted-copy">{tournament.business_name} · {tournament.starts_at ? new Date(tournament.starts_at).toLocaleDateString("es-ES") : "Fecha por confirmar"}</div>
                </div>
                <Badge bg="light" text="dark">{tournament.points} pts</Badge>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {activeProfileTab === "reviews" && (
        <div className="d-grid gap-3">
          {reviews.length === 0 && <Card className="cardora-card"><Card.Body>Aun no recibio valoraciones.</Card.Body></Card>}
          {reviews.map(review => (
            <Card className="cardora-card" key={review.id}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <div className="profile-review-avatar">
                      {review.reviewer_avatar_url ? <img src={mediaUrl(review.reviewer_avatar_url)} alt="" /> : <span>{review.reviewer_name?.slice(0, 1) || "C"}</span>}
                    </div>
                    <div><strong>{review.reviewer_name}</strong><div className="profile-review-stars">{"★".repeat(review.rating)}{"☆".repeat(Math.max(0, 5 - review.rating))}</div></div>
                  </div>
                  <span className="muted-copy small">{new Date(review.created_at).toLocaleDateString("es-ES")}</span>
                </div>
                {review.tags?.length > 0 && (
                  <div className="profile-review-tags mb-2">
                    {review.tags.map(tag => <span key={tag}>{tag}</span>)}
                  </div>
                )}
                <p className="mb-0">{review.comment || "Sin comentario."}</p>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {!binderOnly && connectionStatus.status === "self" && <section className="profile-share-panel mb-4" aria-label={`Compartir contenido de ${profile?.display_name || "este perfil"}`}>
        <span className="profile-share-owner"><i className="bi bi-share me-2" />Compartir contenido de <strong>{profile?.display_name || profile?.handle || "este perfil"}</strong></span>
        <div className="profile-share-actions">
          <Button variant="outline-dark" size="sm" onClick={() => copyShareLink("binder", activeCollectionId)}>
            <i className="bi bi-journal-album me-2" />Binder
          </Button>
          <Button variant="outline-dark" size="sm" onClick={() => copyShareLink("trade")}>
            <i className="bi bi-arrow-left-right me-2" />Disponibles
          </Button>
          <Button variant="outline-dark" size="sm" onClick={() => copyShareLink("wanted")}>
            <i className="bi bi-search-heart me-2" />Buscadas
          </Button>
        </div>
      </section>}

      {!binderOnly && (
        <section className="public-profile-summary-segment mt-4" aria-label={`Resumen público de ${profile?.display_name || "este perfil"}`}>
          <div className="public-profile-summary-heading">
            <h2>Sobre este coleccionista</h2>
          </div>
          <div className="public-profile-stats">
            <div className="public-profile-stat">
              <i className="bi bi-collection" />
              <strong>{stats.cards_count || 0}</strong>
              <span>cartas</span>
            </div>
            <div className="public-profile-stat">
              <i className="bi bi-arrow-left-right" />
              <strong>{stats.completed_trades || 0}</strong>
              <span>trades completados</span>
            </div>
            <div className="public-profile-stat public-profile-rating">
              <i className="bi bi-star" />
              <strong>{stats.average_rating ? `${stats.average_rating}/5` : "Sin rating"}</strong>
              <span>{stats.reviews_count || 0} valoraciones</span>
              {(stats.reputation_tags || []).length > 0 && (
                <div className="public-profile-reputation-tags">
                  {stats.reputation_tags.slice(0, 3).map(tag => (
                    <Badge bg="warning" text="dark" key={tag.tag}>{tag.tag} · {tag.count}</Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="public-profile-stat">
              <i className="bi bi-people" />
              <strong>{socialSummary.followers_count || 0}</strong>
              <span>seguidores</span>
            </div>
          </div>
        </section>
      )}

      {!binderOnly && summary.badges?.length > 0 && (
        <section className="public-profile-badges mt-3" aria-label={`Medallas de ${profile?.display_name || "este perfil"}`}>
          <div className="public-profile-badges-title"><i className="bi bi-award-fill" /><span><strong>Medallas</strong><small>Logros públicos desbloqueados</small></span></div>
          <div className="public-profile-badge-list">
            {summary.badges.map(badge => <span className="public-profile-badge" key={badge.id} title={badge.description}><i className={`bi ${badge.icon_key === "star" ? "bi-star-fill" : "bi-award-fill"}`} />{badge.name}</span>)}
          </div>
        </section>
      )}

      <Modal show={showPostLikes} onHide={() => setShowPostLikes(false)} centered>
        <Modal.Header closeButton><Modal.Title>Me gusta</Modal.Title></Modal.Header>
        <Modal.Body>
          {postLikesLoading && <div className="muted-copy">Cargando...</div>}
          {!postLikesLoading && postLikes.length === 0 && <div className="muted-copy">Todavia no hay reacciones.</div>}
          <div className="profile-likes-list">
            {postLikes.map(like => (
              <Link to={`/u/${like.handle || like.id}`} className="profile-like-row" onClick={() => setShowPostLikes(false)} key={like.id}>
                <div className="profile-comment-avatar">
                  {like.avatar_url ? <img src={mediaUrl(like.avatar_url)} alt="" /> : <span>{like.display_name?.slice(0, 1) || "C"}</span>}
                </div>
                <div><strong>{like.display_name}</strong>{like.handle && <span>@{like.handle}</span>}</div>
              </Link>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer><Button variant="outline-dark" onClick={() => setShowPostLikes(false)}>Cerrar</Button></Modal.Footer>
      </Modal>

      <Modal show={Boolean(selectedCard)} onHide={() => setSelectedCard(null)} centered contentClassName="card-interest-modal">
        <Modal.Header closeButton>
          <Modal.Title>Interes por carta</Modal.Title>
        </Modal.Header>
        <Form onSubmit={proposeTrade}>
          <Modal.Body>
            {canBuildTrade(selectedCard) && <div className="trade-proposal-steps" aria-label={`Paso ${tradeStep} de 4`}>
              {["Carta", "Oferta", "Acuerdo", "Enviar"].map((label, index) => (
                <span className={tradeStep > index + 1 ? "is-complete" : tradeStep === index + 1 ? "is-active" : ""} key={label}><b>{index + 1}</b>{label}</span>
              ))}
            </div>}
            {(!canBuildTrade(selectedCard) || tradeStep === 1) && <div className="trade-wizard-panel">
              <span className="section-kicker">Carta seleccionada</span>
              <div className="trade-wizard-target">
                {cardFrontImage(selectedCard) ? <img src={cardFrontImage(selectedCard)} alt={selectedCard?.name || ""} /> : <span className="card-image-fallback"><i className="bi bi-card-image" /></span>}
                <div><strong>{selectedCard?.name}</strong><small>{[selectedCard?.set_name, selectedCard?.collector_number].filter(Boolean).join(" - ")}</small></div>
              </div>
              <div className="card-interest-context mb-0">
                <strong>{isConnectedWithProfile ? "Ya estais conectados" : "Conexion por carta"}</strong>
                <span>{isConnectedWithProfile ? "Le avisaremos que esta carta te interesa." : "Esto abre una conversacion contextual, no una venta directa."}</span>
              </div>
            </div>}
            {(!canBuildTrade(selectedCard) || tradeStep === 3) && <div className="card-event-context mb-3">
              <strong>Eventos para coincidir</strong>
              {cardEventContext.loading && <span>Buscando eventos en comun...</span>}
              {!cardEventContext.loading && cardEventContext.sharedEvents.length > 0 && (
                <div className="card-event-list">
                  {cardEventContext.sharedEvents.map(event => (
                    <Link to={eventDetailPath(event)} key={event.id} className="card-event-chip">
                      <i className="bi bi-calendar-check" />
                      <span>{event.name}</span>
                      <small>{event.business_name} · {formatEventDate(event.starts_at)}</small>
                    </Link>
                  ))}
                </div>
              )}
              {!cardEventContext.loading && cardEventContext.sharedEvents.length === 0 && (
                <>
                  <span>Aun no detectamos eventos compartidos para esta carta.</span>
                  {cardEventContext.suggestedEvents.length > 0 && (
                    <div className="card-event-list mt-2">
                      {cardEventContext.suggestedEvents.slice(0, 3).map(event => (
                        <Link to={eventDetailPath(event)} key={event.id} className="card-event-chip is-suggested">
                          <i className="bi bi-calendar-plus" />
                          <span>{event.name}</span>
                          <small>{event.business_name} · {formatEventDate(event.starts_at)}</small>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>}
            {canBuildTrade(selectedCard) && tradeStep === 2 ? (
              <div className="trade-builder">
                <div className="trade-builder-side is-requested">
                  <span>{selectedCard?.status === "wanted" ? "Esta persona busca" : "Quiero"}</span>
                  <div className="trade-target-card">
                    <img src={cardFrontImage(selectedCard)} alt="" />
                    <strong>{selectedCard?.name}</strong>
                  </div>
                </div>
                <i className="bi bi-arrow-left-right trade-builder-arrow" />
                <div className="trade-builder-side">
                  <span>Ofrezco <small>{offeredCardIds.length}/3</small></span>
                  <div className="trade-offer-slots">
                    {[0, 1, 2].map(index => {
                      const cardId = offeredCardIds[index];
                      const card = myCards.find(item => item.id === cardId);
                      return card ? (
                        <button type="button" className="trade-offer-slot is-filled" onClick={() => setOfferedCardIds(current => current.filter(id => id !== cardId))} key={cardId}>
                          <img src={cardFrontImage(card)} alt="" />
                          <small>{card.name}</small>
                          <i className="bi bi-x-circle-fill" />
                        </button>
                      ) : (
                        <div className="trade-offer-slot" key={index}><i className="bi bi-plus-lg" /><small>Agregar</small></div>
                      );
                    })}
                  </div>
                </div>
                <div className="trade-card-picker">
                  <div className="trade-card-picker-filters">
                    <Form.Control value={tradeSearch} onChange={event => setTradeSearch(event.target.value)} placeholder="Buscar en mis cartas..." />
                    <Form.Select value={tradeBinderFilter} onChange={event => setTradeBinderFilter(event.target.value)}>
                      <option value="all">Todos mis binders</option>
                      {myCollections.map(collection => <option value={collection.id} key={collection.id}>{collection.name}</option>)}
                    </Form.Select>
                  </div>
                  <div className="trade-card-picker-grid">
                    {myCards
                      .filter(isTradeableCard)
                      .filter(card => tradeBinderFilter === "all" || card.collection_id === tradeBinderFilter)
                      .filter(card => !tradeSearch.trim() || [card.name, card.set_name, card.collector_number].some(value => String(value || "").toLowerCase().includes(tradeSearch.trim().toLowerCase())))
                      .sort((left, right) => Number(isWantedByProfile(right)) - Number(isWantedByProfile(left)))
                      .map(card => {
                        const selected = offeredCardIds.includes(card.id);
                        return (
                          <button type="button" className={selected ? "is-selected" : ""} disabled={!selected && offeredCardIds.length >= 3} onClick={() => setOfferedCardIds(current => selected ? current.filter(id => id !== card.id) : [...current, card.id])} key={card.id}>
                            <img src={cardFrontImage(card)} alt="" />
                            <span><strong>{card.name}</strong><small>{isWantedByProfile(card) ? "La esta buscando · " : ""}{card.set_name || "Sin set"} - {card.condition || "Sin condicion"}</small></span>
                            <i className={selected ? "bi bi-check-circle-fill" : "bi bi-plus-circle"} />
                          </button>
                        );
                      })}
                  </div>
                </div>
              </div>
            ) : !canBuildTrade(selectedCard) ? (
              <div className="card-interest-context mb-3">
                <strong>Esta copia no esta ofrecida activamente</strong>
                <span>Enviaremos un aviso de interes al propietario, sin crear una oferta formal.</span>
              </div>
            ) : null}
            {(!canBuildTrade(selectedCard) || tradeStep === 3) && <Form.Group className="trade-wizard-panel">
              <Form.Label>Mensaje para {profile?.display_name}</Form.Label>
              <Form.Control as="textarea" rows={4} value={message} onChange={event => setMessage(event.target.value)} placeholder="Me interesa conectar por esta carta. Podemos verlo en un evento o acordar un punto." />
              <Form.Text>Los detalles finales se acuerdan entre ambas personas.</Form.Text>
            </Form.Group>}
            {canBuildTrade(selectedCard) && tradeStep === 4 && <div className="trade-wizard-summary">
              <span className="section-kicker">Resumen</span>
              <h3>Revisa antes de enviar</h3>
              <div><span>Te interesa</span><strong>{selectedCard?.name}</strong></div>
              <div><span>Ofreces</span><strong>{offeredCardIds.length ? offeredCardIds.map(id => myCards.find(card => card.id === id)?.name).filter(Boolean).join(", ") : "Propuesta abierta"}</strong></div>
              <div><span>Mensaje</span><strong>{message.trim() || "Sin mensaje"}</strong></div>
            </div>}
          </Modal.Body>
          <Modal.Footer className="card-interest-modal-actions">
            <Button variant="light" className="cardora-soft-action" onClick={() => tradeStep > 1 && canBuildTrade(selectedCard) ? setTradeStep(step => step - 1) : setSelectedCard(null)}>{tradeStep > 1 && canBuildTrade(selectedCard) ? "Atras" : "Cerrar"}</Button>
            {canBuildTrade(selectedCard) && tradeStep < 4 ? (
              <Button type="button" className="btn-cardora" disabled={tradeStep === 2 && selectedCard?.status === "wanted" && offeredCardIds.length === 0} onClick={() => setTradeStep(step => Math.min(4, step + 1))}>Continuar <i className="bi bi-arrow-right ms-2" /></Button>
            ) : (
              <Button type="submit" className="btn-cardora" disabled={tradeSubmitting}>
                {tradeSubmitting && <Spinner animation="border" size="sm" className="me-2" />}
                {tradeSubmitting ? "Enviando..." : canBuildTrade(selectedCard) ? "Confirmar y enviar" : "Avisar interes"}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showMessageModal} onHide={() => setShowMessageModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Mensaje privado</Modal.Title>
        </Modal.Header>
        <Form onSubmit={sendPrivateMessage}>
          <Modal.Body>
            <p className="muted-copy">Para: <strong>{profile?.display_name}</strong></p>
            <Form.Group className="mb-3">
              <Form.Label>Asunto</Form.Label>
              <Form.Control
                value={privateMessage.subject}
                onChange={event => setPrivateMessage({ ...privateMessage, subject: event.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Mensaje</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={privateMessage.body}
                onChange={event => setPrivateMessage({ ...privateMessage, body: event.target.value })}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-dark" onClick={() => setShowMessageModal(false)}>Cerrar</Button>
            <Button type="submit" className="btn-cardora">Enviar</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showCardPreview}
        onHide={closeCardPreview}
        centered
        size="lg"
        contentClassName="public-card-preview-modal binder-mobile-card-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{activeBinderCard?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeBinderCard && (
            <>
              {activeBinderCard.id && !activeBinderCard.is_missing && <p className="mb-2">{binderQuantityLabel(activeBinderCard, tradeOnlyQuantity)}</p>}
              <div className="binder-mobile-card-navigation" aria-label="Navegar cartas del binder">
                <Button variant="outline-dark" className="binder-icon-button" onClick={() => movePublicBinderCard(-1)} aria-label="Carta anterior"><i className="bi bi-chevron-left" /></Button>
                <span>{Math.max(0, previewCards.findIndex(card => publicCardKey(card) === publicCardKey(activeBinderCard))) + 1} de {previewCards.length}</span>
                <Button variant="outline-dark" className="binder-icon-button" onClick={() => movePublicBinderCard(1)} aria-label="Carta siguiente"><i className="bi bi-chevron-right" /></Button>
              </div>
              <BinderCardViewer
              name={activeBinderCard.name}
              frontImage={cardFrontImage(activeBinderCard)}
              backImage={cardBackImage(activeBinderCard)}
              side={binderPreviewSide}
              onRotate={rotatePublicCard}
              isZoomed={isBinderPreviewZoomed}
              zoomOrigin={binderPreviewZoomOrigin}
              onMoveZoom={moveBinderPreviewZoom}
              onResetZoom={resetBinderPreviewZoom}
              onToggleZoom={() => setIsBinderPreviewZoomed(current => !current)}
              className={`public-card-preview-stage binder-mobile-card-stage ${activeBinderCard.is_missing ? "is-missing" : ""}`}
              imageClassName="public-card-modal-image"
            />
              <div className="binder-mobile-card-meta public-binder-card-meta">
                <Badge bg={activeBinderCard.status === "for_trade" ? "warning" : "success"} text={activeBinderCard.status === "for_trade" ? "dark" : undefined}>
                  {intentionLabels[activeBinderCard.card_intention] || statusLabels[activeBinderCard.status] || activeBinderCard.status}
                </Badge>
                <span>{[activeBinderCard.set_name, activeBinderCard.collector_number].filter(Boolean).join(" - ")}</span>
                <RarityMark rarity={activeBinderCard.rarity} />
                {cardFinishLabels[activeBinderCard.card_finish] && <span>{cardFinishLabels[activeBinderCard.card_finish]}</span>}
                <span>{[activeBinderCard.card_type, activeBinderCard.energy_type].filter(Boolean).join(" / ") || "Sin tipo"}</span>
              </div>
              {wantedMatchResult?.cardId === activeBinderCard.id && <div className={`card-match-result ${wantedMatchResult.matches > 0 ? "has-matches" : ""}`} role="status"><i className={`bi ${wantedMatchResult.matches > 0 ? "bi-stars" : "bi-search-heart"}`} /><div><strong>{!wantedMatchResult.evaluationOk ? "Carta guardada, pero no pudimos buscar coincidencias" : wantedMatchResult.matches > 0 ? "Coincidencia encontrada" : wantedMatchResult.created ? "Busqueda activada" : "Ya estaba en tus buscadas"}</strong><span>{!wantedMatchResult.evaluationOk ? wantedMatchResult.error || "Intentalo de nuevo mas tarde." : wantedMatchResult.matches > 0 ? `${wantedMatchResult.matches} ${wantedMatchResult.matches === 1 ? "persona tiene" : "personas tienen"} una copia disponible.` : wantedMatchResult.created ? "La compararemos automaticamente con las cartas disponibles." : "Revisa Intereses para ver sus coincidencias activas."}</span></div>{(wantedMatchResult.matches > 0 || !wantedMatchResult.created) && <Button as={Link} to="/trades" size="sm" className="btn-cardora" onClick={closeCardPreview}>Ver intereses</Button>}</div>}
            </>
          )}
        </Modal.Body>
        {activeBinderCard && (
          <Modal.Footer className="public-card-preview-actions">
            {connectionStatus.status !== "self" && !activeBinderCard.is_missing && activeBinderCard.status !== "wanted" && (
              <Button
                variant={activeBinderCard.viewer_liked ? "danger" : "outline-light"}
                className="public-card-like-action"
                onClick={() => toggleCardLike(activeBinderCard)}
              >
                <i className={`bi ${activeBinderCard.viewer_liked ? "bi-heart-fill" : "bi-heart"} me-2`} />
                {activeBinderCard.viewer_liked ? "Te gusta" : "Me gusta"} · {activeBinderCard.likes_count || 0}
              </Button>
            )}
            {localStorage.getItem("token") && connectionStatus.status !== "self" && activeBinderCard.is_missing && !activeBinderCard.id && (
              <Button className="btn-cardora" onClick={() => openActionFromPreview(card => openWantedOfferModal({ ...card, status: "wanted" }))}>
                <i className="bi bi-hand-thumbs-up me-2" />Yo la tengo
              </Button>
            )}
            {localStorage.getItem("token") && connectionStatus.status !== "self" && ["for_trade", "for_sale", "showcase", "favorite"].includes(activeBinderCard.status) && (
              <Button variant="light" className="public-card-secondary-action" onClick={() => markCardAsWanted(activeBinderCard)}>
                <i className="bi bi-search-heart me-2" />Añadir a buscadas
              </Button>
            )}
            {localStorage.getItem("token") && connectionStatus.status !== "self" && ["for_trade", "for_sale", "showcase"].includes(activeBinderCard.status) && (
              <Button
                className="btn-cardora"
                disabled={isPendingOutgoingConnection && !isTradeableCard(activeBinderCard)}
                onClick={() => {
                  if (isTradeableCard(activeBinderCard)) { closeCardPreview(); openTradeModal(activeBinderCard); return; }
                  if (isConnectedWithProfile) {
                    closeCardPreview();
                    handleCardPrimaryAction(activeBinderCard);
                    return;
                  }
                  if (isPendingIncomingConnection) {
                    closeCardPreview();
                    acceptConnection();
                    return;
                  }
                  if (isPendingOutgoingConnection) {
                    setNotice("La solicitud de conexion ya esta enviada.");
                    return;
                  }
                  openActionFromPreview(openTradeModal);
                }}
              >
                {cardPrimaryLabel(activeBinderCard)}
              </Button>
            )}
            {localStorage.getItem("token") && connectionStatus.status !== "self" && activeBinderCard.status === "wanted" && !activeBinderCard.is_missing && (
              <Button className="btn-cardora" onClick={() => openActionFromPreview(openWantedOfferModal)}>
                Tengo una carta que buscas
              </Button>
            )}
            {localStorage.getItem("token") && connectionStatus.status !== "self" && activeBinderCard.status === "showcase" && isConnectedWithProfile && (
              <Button variant="light" className="public-card-secondary-action" onClick={() => { closeCardPreview(); setShowMessageModal(true); }}>Enviar mensaje</Button>
            )}
            {!localStorage.getItem("token") && (
              <Button as={Link} to={registerForCard(activeBinderCard, activeBinderCard.is_missing || activeBinderCard.status === "wanted" ? "have" : "interest")} className="btn-cardora" onClick={closeCardPreview}>{activeBinderCard.is_missing || activeBinderCard.status === "wanted" ? "Registrarme para ofrecer mi carta" : "Registrarme para proponer intercambio"}</Button>
            )}
            <Button as={Link} to={`/u/${profile?.handle || profile?.id || userIdOrHandle}`} variant="light" className="public-card-secondary-action" onClick={closeCardPreview}><i className="bi bi-person me-2" />Ver perfil</Button>
          </Modal.Footer>
        )}
      </Modal>
    </Container>
  );
}

export default PublicBinder;
